/**
 * Toast — minimal viewport-anchored confirmation pattern matching the
 * locked grammar in /design/V1_Send_Invite_Pattern_Spec.md §"Toast
 * confirmation pattern".
 *
 * Two registers — `tip` (success) and `watch_out` (error) — mapped to the
 * locked Callout palette. Stacks at bottom-right; success toasts auto-dismiss
 * after 4 seconds; error toasts persist until manual dismiss. Both registers
 * have a Close (×) affordance so the user can dismiss any toast on demand.
 *
 * Usage:
 *   import { ToastProvider, useToast } from "./components/Toast.jsx";
 *
 *   // Wrap the app once:
 *   <ToastProvider>...</ToastProvider>
 *
 *   // Anywhere downstream:
 *   const toast = useToast();
 *   toast.success("Invite sent to jimmy@example.com.", "Expires in 7 days. You can resend after 24 hours.");
 *   toast.error("Couldn't send the invite. The profile is still saved as Lite.");
 *
 * Two-line max body per spec: first line = action confirmation, second
 * line = next-step hint. The `subline` argument is optional and renders
 * smaller below the headline.
 *
 * Why an in-tree component rather than an external library: V1 dependency
 * discipline — no bare `react-toastify` or similar. Toast is small enough
 * to own and the spec dictates exact registers anyway.
 */

import { createContext, useContext, useState, useCallback, useEffect } from "react";

const ToastContext = createContext(null);

const SUCCESS_AUTO_DISMISS_MS = 4000;

let toastIdCounter = 0;
function nextId() {
  toastIdCounter += 1;
  return `toast_${toastIdCounter}`;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((toast) => {
    const id = nextId();
    const t = { id, ...toast };
    setToasts((current) => [...current, t]);
    if (t.kind === "tip") {
      // Success register auto-dismisses after 4s. Error register persists
      // until the user clicks × — error states are higher-stakes and
      // we don't want them to vanish while the user is still reading.
      setTimeout(() => {
        setToasts((current) => current.filter((x) => x.id !== id));
      }, SUCCESS_AUTO_DISMISS_MS);
    }
    return id;
  }, []);

  const api = {
    success: (headline, subline) => push({ kind: "tip", headline, subline }),
    error: (headline, subline) => push({ kind: "watch_out", headline, subline }),
    dismiss,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
}

/**
 * Hook used by any component that wants to push a toast. Returns
 * `{ success, error, dismiss }`.
 *
 * Falls back to a no-op API if used outside a ToastProvider — that lets
 * components ship the call without crashing in test renders that don't
 * mount the provider.
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (ctx) return ctx;
  return {
    success: () => null,
    error: () => null,
    dismiss: () => null,
  };
}

// ---------------------------------------------------------------------------
// Viewport — fixed bottom-right, stacks newest below older toasts.
// ---------------------------------------------------------------------------

function ToastViewport({ toasts, dismiss }) {
  if (!toasts || toasts.length === 0) return null;
  return (
    <div
      role="region"
      aria-label="Notifications"
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        maxWidth: 420,
        pointerEvents: "none",
      }}
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastCard({ toast, onDismiss }) {
  const isError = toast.kind === "watch_out";
  return (
    <div
      role={isError ? "alert" : "status"}
      data-testid={`toast-${toast.kind}`}
      style={{
        pointerEvents: "auto",
        background: isError
          ? "var(--ppr-color-accent-coral-very-soft)"
          : "var(--ppr-color-background-surface)",
        borderLeft: isError
          ? "4px solid var(--ppr-color-accent-coral)"
          : "4px solid var(--ppr-color-accent-navy)",
        padding: "12px 14px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.10)",
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: 11,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          color: isError
            ? "var(--ppr-color-accent-coral-dark)"
            : "var(--ppr-color-accent-navy)",
          marginBottom: 4,
        }}>
          {isError ? "Watch out" : "Tip"}
        </div>
        <div style={{
          fontFamily: "var(--ppr-typography-font-family-body)",
          fontSize: "var(--ppr-typography-font-size-sm)",
          lineHeight: "var(--ppr-typography-line-height-relaxed)",
          color: isError
            ? "var(--ppr-color-accent-coral-dark)"
            : "var(--ppr-color-text-primary)",
        }}>
          {toast.headline}
        </div>
        {toast.subline && (
          <div style={{
            fontFamily: "var(--ppr-typography-font-family-mono)",
            fontSize: "11px",
            color: "var(--ppr-color-text-muted)",
            marginTop: 4,
          }}>
            {toast.subline}
          </div>
        )}
      </div>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: "transparent",
          border: 0,
          padding: 4,
          cursor: "pointer",
          color: "var(--ppr-color-text-muted)",
          fontFamily: "var(--ppr-typography-font-family-mono)",
          fontSize: 16,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}

// useEffect kept imported for future hooks expansion (e.g., focus
// management on toast open). Not currently used.
void useEffect;
